import { type Component, createEffect, createSignal, For, onCleanup, Show } from 'solid-js'
import Button from '~/components/Button'
import Icon from '~/components/Icon'
import { VoiceCall, type VoiceCallStatus } from '~/utils/voice'

interface VoiceCallPanelPropsType {
  /** URL del WebSocket de voz (autenticado o público). */
  url: string
  name: string
  avatar?: string | null
  /** Compacto para el widget de la web; amplio para el panel. */
  compact?: boolean
}

interface LineType {
  role: 'user' | 'assistant'
  text: string
}

const STATUS_TEXT: Record<VoiceCallStatus, string> = {
  idle: 'Listo para llamar',
  connecting: 'Conectando…',
  live: 'En llamada',
  ended: 'Llamada finalizada',
  error: 'No se pudo conectar'
}

/**
 * Interfaz de "llamada" para un agente de voz: en vez del chat de texto, un botón
 * para hablar y escuchar. Las transcripciones se van apuntando debajo para dejar
 * rastro de lo que se dijo.
 */
const VoiceCallPanel: Component<VoiceCallPanelPropsType> = props => {
  const [status, setStatus] = createSignal<VoiceCallStatus>('idle')
  const [detail, setDetail] = createSignal('')
  const [speaking, setSpeaking] = createSignal(false)
  const [lines, setLines] = createSignal<LineType[]>([])

  let call: VoiceCall | undefined

  const appendTranscript = (role: 'user' | 'assistant', text: string) => {
    if (!text.trim()) return
    // Gemini manda la transcripción en trozos; se van pegando al último turno del
    // mismo hablante en vez de crear una línea por fragmento.
    setLines(prev => {
      const last = prev[prev.length - 1]
      if (last && last.role === role) {
        const next = [...prev]
        next[next.length - 1] = { role, text: last.text + text }
        return next
      }
      return [...prev, { role, text }]
    })
  }

  const startCall = async () => {
    setLines([])
    setDetail('')
    call = new VoiceCall(props.url, {
      onStatus: (s, d) => {
        setStatus(s)
        if (d) setDetail(d)
      },
      onTranscript: appendTranscript,
      onSpeakingChange: setSpeaking
    })
    await call.start()
  }

  const hangup = () => {
    call?.hangup()
    call = undefined
  }

  onCleanup(hangup)

  const inCall = () => status() === 'connecting' || status() === 'live'

  const avatarSize = () => (props.compact ? 'h-20 w-20 text-3xl' : 'h-28 w-28 text-5xl')

  // El transcript baja solo a la línea nueva; si no, lo último queda bajo el borde.
  let transcriptBox: HTMLDivElement | undefined
  createEffect(() => {
    lines()
    requestAnimationFrame(() => {
      if (transcriptBox) transcriptBox.scrollTop = transcriptBox.scrollHeight
    })
  })

  return (
    // min-h-0 deja que el transcript (flex-1) scrollee dentro en vez de empujar
    // el avatar y el botón fuera de la tarjeta.
    <div class="flex flex-col flex-1 min-h-0">
      {/* Cabecera fija: avatar, estado y el botón de llamar/colgar. */}
      <div class="flex flex-col items-center gap-4 shrink-0 pt-5 pb-3 px-4" classList={{ 'pt-8 pb-4': !props.compact }}>
        <div class="relative flex items-center justify-center">
          <Show when={speaking()}>
            <span class="absolute inline-flex h-20 w-20 animate-ping rounded-full bg-primary-500/30" classList={{ 'h-32 w-32': !props.compact }} />
          </Show>
          <div class={`relative flex items-center justify-center rounded-full bg-primary-600 shadow-lg ${avatarSize()}`} classList={{ 'ring-4 ring-primary-400': status() === 'live' }}>
            <Show when={props.avatar} fallback={<Icon icon="material-symbols-light:record-voice-over-outline" size={props.compact ? 32 : 44} class="text-white" />}>
              {props.avatar}
            </Show>
          </div>
        </div>

        <div class="text-center">
          <p class="text-lg font-semibold text-foreground">{props.name}</p>
          <p class="text-sm text-foreground-subtle">
            {STATUS_TEXT[status()]}
            <Show when={detail()}>
              {' · '}
              {detail()}
            </Show>
          </p>
          <Show when={status() === 'live'}>
            <p class="text-xs text-foreground-subtle mt-1">{speaking() ? 'Hablando…' : 'Te escucho'}</p>
          </Show>
        </div>

        <Show
          when={inCall()}
          fallback={
            <Button size="lg" pill prependIcon="material-symbols-light:phone-in-talk" onClick={startCall}>
              {status() === 'ended' || status() === 'error' ? 'Volver a llamar' : 'Llamar'}
            </Button>
          }
        >
          <Button size="lg" pill color="danger" prependIcon="material-symbols-light:call-end" onClick={hangup}>
            Colgar
          </Button>
        </Show>
      </div>

      {/* Transcripción: la única zona que scrollea, ocupa el resto del alto. */}
      <Show
        when={lines().length}
        fallback={<p class="shrink-0 px-4 pb-4 text-xs text-foreground-subtle text-center">Habla con naturalidad; puedes interrumpir en cualquier momento. El micrófono se apaga al colgar.</p>}
      >
        <div ref={transcriptBox} class="flex-1 min-h-0 overflow-y-auto space-y-2 border-t border-border bg-surface-muted/30 px-4 py-3">
          <For each={lines()}>
            {line => (
              <p class="text-sm">
                <span class="font-medium" classList={{ 'text-primary-500': line.role === 'assistant', 'text-foreground-subtle': line.role === 'user' }}>
                  {line.role === 'assistant' ? props.name : 'Tú'}:
                </span>{' '}
                <span class="text-foreground">{line.text}</span>
              </p>
            )}
          </For>
        </div>
      </Show>
    </div>
  )
}

export default VoiceCallPanel
